import { useCallback, useRef, useState, type FormEvent } from "react";

type Status = "idle" | "loading" | "ok" | "err";

const ENDPOINT = import.meta.env.VITE_SIGNUP_ENDPOINT ?? null;
const COOLDOWN_MS = 4000;

type Options = {
  onSuccess?: () => void;
};

export function useSignupForm(formName: string, options: Options = {}) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const lastSubmitAt = useRef<number>(0);
  const inFlight = useRef<boolean>(false);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (inFlight.current) return;
      const now = Date.now();
      if (now - lastSubmitAt.current < COOLDOWN_MS) {
        const wait = Math.ceil((COOLDOWN_MS - (now - lastSubmitAt.current)) / 1000);
        setStatus("err");
        setMessage(`Even geduld — wacht nog ${wait} seconde${wait === 1 ? "" : "n"} voor een nieuwe poging.`);
        return;
      }

      const form = e.currentTarget;
      const data = Object.fromEntries(new FormData(form).entries()) as Record<string, string>;

      if (!data.email || !/.+@.+\..+/.test(data.email)) {
        setStatus("err");
        setMessage("Vul een geldig e-mailadres in.");
        return;
      }

      inFlight.current = true;
      setStatus("loading");
      setMessage("Bezig met versturen…");

      try {
        if (ENDPOINT) {
          const res = await fetch(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ ...data, _form: formName, _ts: new Date().toISOString() }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
        } else {
          const key = "mh_submissions";
          const log = JSON.parse(localStorage.getItem(key) || "[]");
          log.push({ form: formName, ts: new Date().toISOString(), ...data });
          localStorage.setItem(key, JSON.stringify(log));
          await new Promise((r) => setTimeout(r, 400));
        }
        const childName = (data.child_name ?? "").trim();
        const dateLabel = (data.workshop_event_date ?? "").trim();
        const okMsg = dateLabel
          ? `✅ Bedankt! Inschrijving voor ${childName || "je kind"} op ${dateLabel} ontvangen. Check je inbox voor de bevestigingsmail met agenda-link.`
          : `✅ Bedankt! ${childName ? `${childName} staat ` : "Je staat "}op de wachtlijst — check je inbox voor de bevestigingsmail.`;
        setStatus("ok");
        setMessage(okMsg);
        form.reset();
        lastSubmitAt.current = Date.now();
        options.onSuccess?.();
      } catch {
        setStatus("err");
        setMessage(
          "Er ging iets mis bij het versturen. Probeer opnieuw of mail hallo.contact@makershub.be.",
        );
      } finally {
        inFlight.current = false;
      }
    },
    [formName, options],
  );

  const isSubmitting = status === "loading";

  return { status, message, handleSubmit, isSubmitting };
}
